import type { InscricaoPushEntrada } from '@mymaxx/shared';
import { chatApi } from './chat-api';
import { internoApi } from './interno-api';

/**
 * Aviso no celular quando chega mensagem e o app está fechado.
 *
 * O técnico de campo abre o atendimento, guarda o celular no bolso e vai trabalhar
 * (pedido de 29/09). Sem isto ele teria de manter a tela acesa para não perder a resposta.
 *
 * São duas inscrições, e não uma: a do atendimento pertence ao protocolo aberto e morre
 * com ele; a da conversa interna pertence à conta e vale enquanto ele estiver logado
 * (06/10). O mesmo aparelho pode estar nas duas — são avisos de coisas diferentes, e
 * nenhum chega duplicado por isso.
 *
 * Tudo aqui falha em silêncio de propósito: push é um extra. Navegador sem suporte,
 * permissão negada, servidor sem chave configurada — em qualquer um desses casos o chat
 * continua igual, recebendo pelo WebSocket enquanto a tela estiver aberta.
 */

const CHAVE_PEDIDO = 'mymaxx:push-pedido';

/**
 * A chave VAPID vem em base64url e o navegador quer bytes.
 *
 * O retorno é o ArrayBuffer, e não a view: a assinatura de `applicationServerKey` aceita
 * `BufferSource`, e um `Uint8Array` tipado com `ArrayBufferLike` não encaixa ali.
 */
function paraBytes(base64url: string): ArrayBuffer {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bruto = atob(base64);
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes.buffer;
}

function suportado(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Pede a permissão (uma vez só) e devolve a inscrição deste aparelho.
 *
 * `null` quando não dá: sem suporte, sem chave no servidor, permissão negada, ou pedido já
 * feito uma vez e recusado. Insistir a cada tela irrita e não muda nada — quem negou não
 * volta atrás por um segundo pedido, e o navegador nem pergunta de novo.
 */
async function inscricaoDoAparelho(): Promise<InscricaoPushEntrada | null> {
  if (!suportado()) return null;
  const { chave } = await chatApi.chavePush();
  // Sem chave no servidor o push está desligado: não custa nada sair calado.
  if (!chave) return null;

  if (Notification.permission === 'denied') return null;
  if (Notification.permission === 'default') {
    if (localStorage.getItem(CHAVE_PEDIDO) === 'feito') return null;
    localStorage.setItem(CHAVE_PEDIDO, 'feito');
    if ((await Notification.requestPermission()) !== 'granted') return null;
  }

  const registro = await navigator.serviceWorker.ready;
  const inscricao =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      // Obrigatório nos navegadores de hoje: toda notificação precisa ser visível para a
      // pessoa. Push silencioso para rastrear alguém não é permitido — e não é o que
      // queremos.
      userVisibleOnly: true,
      applicationServerKey: paraBytes(chave),
    }));

  const json = inscricao.toJSON();
  if (!json.keys?.p256dh || !json.keys.auth) return null;
  return { endpoint: inscricao.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

/**
 * Inscreve o aparelho neste atendimento.
 *
 * Só deve ser chamado depois de a pessoa entrar no atendimento — pedir permissão na
 * abertura da página é o caminho mais curto para ela negar para sempre.
 */
export async function ativarAvisoDeMensagem(): Promise<void> {
  try {
    const inscricao = await inscricaoDoAparelho();
    if (inscricao) await chatApi.inscreverPush(inscricao);
  } catch {
    /* sem aviso, o chat segue igual */
  }
}

/** Inscreve o aparelho nas conversas internas — pela conta, não por atendimento. */
export async function ativarAvisoInterno(): Promise<void> {
  try {
    const inscricao = await inscricaoDoAparelho();
    if (inscricao) await internoApi.inscreverPush(inscricao);
  } catch {
    /* sem aviso, a conversa segue chegando pelo WebSocket com a tela aberta */
  }
}
